import { type ReactNode, type RefObject, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Headphones, ListChecks } from 'lucide-react';
import type {
  PodcastActionItem,
  PodcastEpisodeView,
  PodcastTranscriptLine,
} from '@almanac/contracts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { formatTimestampDate } from '../../lib/format';
import {
  FEED_LABELS,
  claimPlayback,
  formatDuration,
  refLink,
  releasePlayback,
} from '../../lib/podcast';
import { type DetailState, useEpisodeDetail } from './hooks';

/** Feed badge, publish date and length, on one line. */
export function EpisodeMeta({ episode }: { episode: PodcastEpisodeView }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
      <Badge variant="secondary">{FEED_LABELS[episode.feed]}</Badge>
      <span>{formatTimestampDate(episode.published_at)}</span>
      <span aria-hidden>·</span>
      <span>{formatDuration(episode.duration_s)}</span>
    </div>
  );
}

function EpisodePlayer({
  episode,
  audio,
  className,
}: {
  episode: PodcastEpisodeView;
  audio: RefObject<HTMLAudioElement | null>;
  className?: string;
}) {
  useEffect(() => {
    const player = audio.current;
    return () => {
      if (player) releasePlayback(player);
    };
  }, [audio]);
  return (
    // The transcript is rendered as text beside the player, with seekable
    // lines; a caption track would show nothing on an <audio> element.
    // oxlint-disable-next-line jsx-a11y/media-has-caption
    <audio
      ref={audio}
      controls
      preload="none"
      src={episode.mp3_url}
      onPlay={(event) => claimPlayback(event.currentTarget)}
      className={cn('w-full', className)}
      aria-label={`Play ${episode.title}`}
    />
  );
}

function useSeek(audio: RefObject<HTMLAudioElement | null>) {
  return (seconds: number) => {
    const player = audio.current;
    if (!player) return;
    player.currentTime = seconds;
    void player.play().catch(() => {});
  };
}

function SectionHeading({ children }: { children: ReactNode }) {
  return <h4 className="text-sm font-medium text-foreground">{children}</h4>;
}

/** The episode's next steps, each with links to the issues and PRs it rests on. */
export function ActionItemList({ items }: { items: PodcastActionItem[] }) {
  return (
    <ol className="space-y-2">
      {items.map((item, index) => (
        <li key={`${index}:${item.text}`} className="flex gap-2.5 text-sm">
          <span className="mt-px font-mono text-xs text-muted-foreground tabular-nums">
            {index + 1}.
          </span>
          <div className="min-w-0">
            <p className="text-foreground">
              {item.text}
              {item.owner ? <span className="text-muted-foreground"> — {item.owner}</span> : null}
            </p>
            {item.refs && item.refs.length > 0 ? (
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs">
                {item.refs.map((ref) => {
                  const link = refLink(ref);
                  if (!link.href) {
                    return (
                      <span key={ref} className="text-muted-foreground">
                        {link.label}
                      </span>
                    );
                  }
                  return (
                    <a
                      key={ref}
                      href={link.href}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary hover:underline"
                    >
                      {link.label}
                    </a>
                  );
                })}
              </div>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
  );
}

function Chapters({
  episode,
  onSeek,
}: {
  episode: PodcastEpisodeView;
  onSeek: (s: number) => void;
}) {
  if (episode.outline.length === 0) return null;
  return (
    <div>
      <SectionHeading>In this episode</SectionHeading>
      <ul className="mt-1 space-y-0.5 text-sm">
        {episode.outline.map((chapter) => (
          <li key={`${chapter.start_s}:${chapter.title}`}>
            <button
              type="button"
              onClick={() => onSeek(chapter.start_s)}
              className="text-left text-foreground hover:text-primary"
            >
              <span className="font-mono text-xs text-primary tabular-nums">{chapter.at}</span>{' '}
              {chapter.title}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Resources({ episode }: { episode: PodcastEpisodeView }) {
  if (episode.resources.length === 0) return null;
  return (
    <div>
      <SectionHeading>Resources</SectionHeading>
      <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm">
        {episode.resources.map((resource) => (
          <li key={`${resource.url}:${resource.title}`}>
            <a
              href={resource.url}
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline"
            >
              {resource.title}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatClock(seconds: number): string {
  const whole = Math.floor(seconds);
  const m = Math.floor(whole / 60);
  const s = String(whole % 60).padStart(2, '0');
  return `${m}:${s}`;
}

/** The spoken script, line by line; a line's time seeks the player there. */
export function TranscriptList({
  lines,
  onSeek,
}: {
  lines: PodcastTranscriptLine[];
  onSeek: (seconds: number) => void;
}) {
  return (
    <ol className="max-h-96 space-y-2 overflow-y-auto pr-1 text-sm">
      {lines.map((line, index) => (
        <li key={`${index}:${line.start_s}`}>
          {line.section ? (
            <p className="mt-3 mb-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
              {line.section}
            </p>
          ) : null}
          <div className="flex gap-2.5">
            <button
              type="button"
              onClick={() => onSeek(line.start_s)}
              className="mt-px shrink-0 font-mono text-xs text-primary tabular-nums hover:underline"
              aria-label={`Play from ${formatClock(line.start_s)}`}
            >
              {formatClock(line.start_s)}
            </button>
            <p className="text-foreground">
              <span className="font-medium">{line.speaker}:</span> {line.text}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function ToggleSection({
  label,
  defaultOpen = false,
  onOpenChange,
  children,
}: {
  label: ReactNode;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}) {
  return (
    <Collapsible defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      <CollapsibleTrigger className="group/toggle flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronDown className="size-4 -rotate-90 transition-transform group-data-[panel-open]/toggle:rotate-0" />
        {label}
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2">{children}</CollapsibleContent>
    </Collapsible>
  );
}

function NextStepsLabel({ count }: { count: number }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <ListChecks className="size-4" />
      {count} next {count === 1 ? 'step' : 'steps'}
    </span>
  );
}

/**
 * Next steps and transcript, once the episode's detail has loaded. Folded,
 * the next steps sit behind a toggle so side-by-side cards stay level.
 */
function EpisodeDetailSections({
  detail,
  onSeek,
  foldNextSteps,
  showTranscript,
}: {
  detail: { state: DetailState; retry: () => void };
  onSeek: (seconds: number) => void;
  foldNextSteps: boolean;
  showTranscript: boolean;
}) {
  const { state, retry } = detail;
  if (state.status === 'idle') return null;
  if (state.status === 'loading') {
    return (
      <div
        className={cn('animate-pulse rounded-lg bg-muted', foldNextSteps ? 'h-5 w-32' : 'h-16')}
        aria-hidden
      />
    );
  }
  if (state.status === 'error') {
    return (
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <p className="text-danger">Could not load next steps: {state.message}</p>
        <Button variant="outline" size="xs" onClick={retry}>
          Try again
        </Button>
      </div>
    );
  }
  const { action_items, transcript } = state.detail;
  return (
    <>
      {action_items.length > 0 && foldNextSteps ? (
        <ToggleSection label={<NextStepsLabel count={action_items.length} />}>
          <ActionItemList items={action_items} />
        </ToggleSection>
      ) : null}
      {action_items.length > 0 && !foldNextSteps ? (
        <div>
          <SectionHeading>Next steps</SectionHeading>
          <div className="mt-2">
            <ActionItemList items={action_items} />
          </div>
        </div>
      ) : null}
      {showTranscript && transcript ? (
        <ToggleSection label="Transcript">
          <TranscriptList lines={transcript} onSeek={onSeek} />
        </ToggleSection>
      ) : null}
    </>
  );
}

/**
 * A full episode: player, summary, chapters, next steps and transcript. The
 * detail loads with the card, since the latest episodes are what people open
 * the page for; next steps start folded so the two columns stay level.
 */
export function FeaturedEpisodeCard({ episode }: { episode: PodcastEpisodeView }) {
  const audio = useRef<HTMLAudioElement>(null);
  const seek = useSeek(audio);
  const detail = useEpisodeDetail(episode, true);
  return (
    <Card className="gap-4 px-4 sm:px-5">
      <div>
        <EpisodeMeta episode={episode} />
        <h3 className="mt-1.5 font-display text-lg font-semibold text-foreground">
          {episode.title}
        </h3>
      </div>
      <EpisodePlayer episode={episode} audio={audio} />
      <p className="text-sm text-foreground">{episode.summary}</p>
      <EpisodeDetailSections detail={detail} onSeek={seek} foldNextSteps showTranscript />
      <ToggleSection label="Chapters and resources">
        <div className="space-y-3">
          <Chapters episode={episode} onSeek={seek} />
          <Resources episode={episode} />
        </div>
      </ToggleSection>
    </Card>
  );
}

/**
 * An archive row: title, player, and show notes that load the next steps and
 * transcript the first time they open.
 */
export function EpisodeCard({ episode }: { episode: PodcastEpisodeView }) {
  const audio = useRef<HTMLAudioElement>(null);
  const seek = useSeek(audio);
  const [open, setOpen] = useState(false);
  const detail = useEpisodeDetail(episode, open);
  return (
    <Card size="sm" className="gap-3 px-4">
      <div>
        <EpisodeMeta episode={episode} />
        <h3 className="mt-1.5 font-medium text-foreground">{episode.title}</h3>
      </div>
      <EpisodePlayer episode={episode} audio={audio} />
      <ToggleSection label="Show notes" onOpenChange={(next) => next && setOpen(true)}>
        <div className="space-y-3">
          <p className="text-sm text-foreground">{episode.summary}</p>
          <Chapters episode={episode} onSeek={seek} />
          <EpisodeDetailSections
            detail={detail}
            onSeek={seek}
            foldNextSteps={false}
            showTranscript
          />
          <Resources episode={episode} />
        </div>
      </ToggleSection>
    </Card>
  );
}

/**
 * A slim episode strip for the digest and home pages: what it is, a player,
 * and its next steps folded away. Links to the Podcast tab for the rest.
 */
export function EpisodeStrip({
  episode,
  heading,
  className,
}: {
  episode: PodcastEpisodeView;
  heading: string;
  className?: string;
}) {
  const audio = useRef<HTMLAudioElement>(null);
  const seek = useSeek(audio);
  const detail = useEpisodeDetail(episode, true);
  return (
    <Card size="sm" className={cn('gap-3 px-4', className)}>
      <div className="flex items-start gap-3">
        <span className="grid size-8 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
          <Headphones className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
            {heading}
          </p>
          <Link
            to="/podcast"
            className="mt-0.5 block font-medium text-foreground hover:text-primary"
          >
            {episode.title}
          </Link>
          <div className="mt-1">
            <EpisodeMeta episode={episode} />
          </div>
        </div>
      </div>
      <EpisodePlayer episode={episode} audio={audio} />
      <EpisodeDetailSections detail={detail} onSeek={seek} foldNextSteps showTranscript={false} />
    </Card>
  );
}
